import React, { useEffect, useRef, useState } from "react";
import InputBox from "../../components/Input";
import LocationIcon from "../../assets/Location.svg";
import MagicLocationIcon from "../../assets/PickUp/magicLocation.svg";
import CustomDropDown from "../../components/DropDown";
import { capitalizeFirstLetter } from "../../utils/utility";
import { Spinner } from "../../components/Spinner";
import AiIcon from "../../assets/Buttons.svg";
import { POST } from "../../utils/webService";
import InfoCircle from "../../assets/info-circle.svg";
import {
  ADD_DELIVERY_ADDRESS,
  ADD_PICKUP_ADDRESS_CATALOGUE,
  GET_PINCODE_DATA,
  VERIFY_ADDRESS,
} from "../../utils/ApiUrls";
import { dummyStateDropdownData } from "../../utils/dummyData";
import { toast } from "react-hot-toast";
import FloatingLabelInput from "../OrderCreation/FloatingLabelInput";
import CloseIcon from "../../assets/CloseIcon.svg";

function AddAddress({ addressType, setAddAddressModal, returnAddress }: any) {
  const [magicAddress, setMagicAddress]: any = useState("");
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsloading] = useState(false);
  const [inputError, setInputError] = useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [phoneValidationErrors, setPhoneValidationErrors] = useState<any>({
    pickup: false,
    delivery: false,
  });
  const [validationErrors, setValidationErrors] = useState<any>({
    pickup: {
      pincode: false,
      pincodeMessage: "",
    },
    delivery: {
      pincode: false,
      pincodeMessage: "",
    },
  });

  const [addAddress, setAddAddress] = useState<any>({
    flatNo: "",
    locality: "",
    landmark: "",
    pincode: "",
    city: "",
    state: "",
    country: "",
    fullAddress: "",
    addressType: "",
    workingDays: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      sunday: true,
    },
    workingHours: "",
    contact: {
      name: "",
      mobileNo: "",
      emailId: "",
      type: "",
    },
  });

  // Reset state when modal opens
  useEffect(() => {
    setHasAttemptedSave(false);
    setInputError(false);
    setAddAddress({
      flatNo: "",
      locality: "",
      landmark: "",
      pincode: "",
      city: "",
      state: "",
      country: "",
      fullAddress: "",
      addressType: "",
      workingDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true,
      },
      workingHours: "",
      contact: {
        name: "",
        mobileNo: "",
        emailId: "",
        type: "",
      },
    });
    setIsDisabled(false);
  }, [addressType]);
  const [errorAddAddressMessage, setErrorAddAddressMessage] = useState<any>({
    addressType: "",
    flatNo: "",
    sector: "",
    landmark: "",
    pincode: "",
    city: "",
    state: "",
    country: "",
    contactName: "",
    mobileNo: "",
  });

  let targetAddress = "";
  if (addressType === "Delivery Address") {
    targetAddress = "deliveryAddress";
  } else if (addressType === "Pickup Address") {
    targetAddress = "pickupAddress";
  }

  const businessTypeDropDown: any = [
    {
      label: "Individual",
      value: "Individual",
    },
    {
      label: "Business",
      value: "Business",
    },
    {
      label: "Company",
      value: "Company",
    },
    {
      label: "Shopkeeper",
      value: "Shopkeeper",
    },
    {
      label: "Others",
      value: "Others",
    },
  ];

  const isValidPhoneNumber = (phone: string): boolean => {
    const cleanPhone = phone.replace(/\D/g, "");
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(cleanPhone);
  };

  const clearValidationError = (addressType: string, field: string) => {
    setValidationErrors((prev: any) => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [field]: false,
        // Also clear pincode message if clearing pincode error
        ...(field === "pincode" && { pincodeMessage: "" }),
      },
    }));
  };

  const handlePincodeChange = (value: string, addressType: string) => {
    const numericValue = value.replace(/[^0-9]/g, "");
    if (numericValue.length <= 6) {
      const pincodeValue = numericValue === "" ? "" : numericValue;
      setAddAddress({
        ...addAddress,
        pincode: pincodeValue,
        // Clear city and state when pincode length is less than 6
        ...(numericValue.length < 6 && {
          city: "",
          state: "",
        }),
      });
      // Clear pincode error message when user starts typing
      setValidationErrors((prev: any) => ({
        ...prev,
        [addressType]: {
          ...prev[addressType],
          pincodeMessage: "",
        },
      }));
      if (numericValue.length === 6) {
        handlePincode(numericValue, addressType);
      } else {
        setIsDisabled(false);
      }
    }
  };

  const handleInputChange = (e: any, nested?: any) => {
    if (nested) {
      setAddAddress({
        ...addAddress,
        [nested]: { ...addAddress[nested], [e.target.name]: e.target.value },
      });
    } else {
      setAddAddress({ ...addAddress, [e.target.name]: e.target.value });
    }

    if (e.target.value !== "") {
      setErrorAddAddressMessage({
        ...errorAddAddressMessage,
        [e.target.name]: "",
      });
    }
  };

  const handlePincode = async (pincode: any, addressType: string) => {
    if (pincode.length === 6) {
      try {
        const { data: response } = await POST(GET_PINCODE_DATA, { pincode });
        const addressTypeKey = addressType === "pickupAddress" ? "pickup" : "delivery";

        if (response?.success && response?.data && response.data.length > 0) {
          const pincodeData = response.data[0];
          // Validate that city and state are not NaN or invalid
          const city = pincodeData.city && 
            pincodeData.city !== "NaN" && 
            String(pincodeData.city).toUpperCase() !== "NAN" &&
            !(typeof pincodeData.city === "number" && isNaN(pincodeData.city))
            ? capitalizeFirstLetter(pincodeData.city)
            : "";
          
          const state = pincodeData.state && 
            pincodeData.state !== "NaN" && 
            String(pincodeData.state).toUpperCase() !== "NAN" &&
            !(typeof pincodeData.state === "number" && isNaN(pincodeData.state))
            ? capitalizeFirstLetter(pincodeData.state)
            : "";

          setAddAddress({
            ...addAddress,
            pincode,
            state: state,
            city: city,
            country: "India",
          });
          setIsDisabled(true);

          // Clear pincode error if successful
          setValidationErrors((prev: any) => ({
            ...prev,
            [addressTypeKey]: {
              ...prev[addressTypeKey],
              pincode: false,
              pincodeMessage: "",
            },
          }));
        } else {
          // Clear city and state if API call fails or returns invalid data
          setAddAddress({
            ...addAddress,
            pincode,
            city: "",
            state: "",
          });
          setIsDisabled(false);

          // Set pincode error message
          setValidationErrors((prev: any) => ({
            ...prev,
            [addressTypeKey]: {
              ...prev[addressTypeKey],
              pincode: true,
              pincodeMessage: "Pincode Is Not Serviceable",
            },
          }));
        }
      } catch (error) {
        console.error("Error in Pincode API", error);
        // Clear city and state on error
        setAddAddress({
          ...addAddress,
          pincode,
          city: "",
          state: "",
        });
        setIsDisabled(false);

        // Set pincode error message
        const addressTypeKey = addressType === "pickupAddress" ? "pickup" : "delivery";
        setValidationErrors((prev: any) => ({
          ...prev,
          [addressTypeKey]: {
            ...prev[addressTypeKey],
            pincode: true,
            pincodeMessage: "Pincode Is Not Serviceable",
          },
        }));
      }
    } else {
      setIsDisabled(false);
    }
  };

  const getVerifyAddress = async (
    verifyAddressPayload: any,
    targetAddress: any
  ) => {
    try {
      setIsAddressLoading(true);

      const { data: verifyAddressResponse } = await POST(
        VERIFY_ADDRESS,
        verifyAddressPayload
      );

      if (verifyAddressResponse?.success) {
        const parsedData = verifyAddressResponse?.data?.message;

        let tempData = {};
        tempData = { ...addAddress };

        tempData = {
          ...tempData,
          flatNo:
            `${parsedData.house_number} ${parsedData.floor} ${parsedData.building_name}` ||
            "",
          fullAddress: parsedData.full_address || "",
          locality: parsedData.locality_name || "",
          sector: parsedData.locality_name || "",
          landmark: parsedData.landmark,
          pincode: parsedData.pincode || "",
          city: parsedData.city_name || "",
          state: capitalizeFirstLetter(parsedData.state_name) || "",
          country: parsedData.country_name || "India",
        };
        setAddAddress(tempData);
      }

      setMagicAddress("");

      setIsAddressLoading(false);
    } catch (error) {
      setIsAddressLoading(false);
      return error;
    }
  };

  const handleButtonClick = (targetAddress: any) => {
    const trimmedData = magicAddress;

    let verifyAddressPayload = {
      data: "",
    };
    verifyAddressPayload.data = magicAddress;

    if (!isAddressLoading && trimmedData !== "") {
      getVerifyAddress(verifyAddressPayload, targetAddress);
    }
  };

  const createAddress = async () => {
    setHasAttemptedSave(true);
    
    if (!addressType) {
      toast.error("Please Select Address Type");
      return;
    }

    // Check if pincode is valid (no error from API)
    const addressTypeKey = targetAddress === "pickupAddress" ? "pickup" : "delivery";
    const isPincodeValid = !validationErrors[addressTypeKey]?.pincode && 
      addAddress?.pincode && 
      String(addAddress?.pincode).length === 6 &&
      addAddress?.city?.trim() &&
      addAddress?.state?.trim();

    // Validate required fields
    if (
      !addAddress?.flatNo?.trim() ||
      !addAddress?.landmark?.trim() ||
      !addAddress?.city?.trim() ||
      !addAddress?.state?.trim() ||
      !addAddress?.country?.trim() ||
      !addAddress?.contact?.name?.trim() ||
      !addAddress?.contact?.mobileNo ||
      String(addAddress?.contact?.mobileNo).trim() === "" ||
      String(addAddress?.contact?.mobileNo).length !== 10 ||
      !addAddress?.pincode ||
      String(addAddress?.pincode).trim() === "" ||
      String(addAddress?.pincode).length !== 6 ||
      !isPincodeValid
    ) {
      setInputError(true);
      return;
    }

    const fullAddress = `${addAddress.flatNo}, ${addAddress.locality}, ${addAddress.landmark}, ${addAddress.city}, ${addAddress.state}, ${addAddress.country}, ${addAddress.pincode}`;

    setAddAddress((prevAddAddress: any) => ({
      ...prevAddAddress,
      fullAddress: fullAddress.trim(),
    }));

    if (addressType === "Pickup Address") {
      setIsloading(true);
      const { data: createAddressBook }: any = await POST(
        ADD_PICKUP_ADDRESS_CATALOGUE,
        {
          ...addAddress,
          addressType: addressType,
          fullAddress: fullAddress.trim(),
          contact: {
            ...addAddress.contact,
            type: "warehouse associate",
          },
        }
      );
      if (createAddressBook?.success) {
        toast.success(createAddressBook?.message);
        setIsloading(false);
        returnAddress();
        setAddAddressModal(false);
        setAddAddress({});
        setHasAttemptedSave(false);
        setInputError(false);
      } else {
        toast.error(createAddressBook?.message);
        setIsloading(false);
      }
    } else {
      setIsloading(true);
      const { data: createDeliveryAddressBook }: any = await POST(
        ADD_DELIVERY_ADDRESS,
        {
          ...addAddress,
          addressType: addressType,
          fullAddress: fullAddress.trim(),
          contact: {
            ...addAddress.contact,
            type: "warehouse associate",
          },
        }
      );
      if (createDeliveryAddressBook?.success) {
        toast.success(createDeliveryAddressBook?.message);
        setIsloading(false);
        returnAddress();
        setAddAddressModal(false);
        setAddAddress({});
        setHasAttemptedSave(false);
        setInputError(false);
      } else {
        toast.error(createDeliveryAddressBook?.message);
        setIsloading(false);
      }
    }
  };

  // Clear errors when user starts typing (only if they've attempted to save)
  useEffect(() => {
    if (hasAttemptedSave) {
      const addressTypeKey = targetAddress === "pickupAddress" ? "pickup" : "delivery";
      const isPincodeValid = !validationErrors[addressTypeKey]?.pincode && 
        addAddress?.pincode && 
        String(addAddress?.pincode).length === 6 &&
        addAddress?.city?.trim() &&
        addAddress?.state?.trim();

      // Check if all required fields are filled
      const allFieldsFilled =
        addAddress?.flatNo?.trim() &&
        addAddress?.landmark?.trim() &&
        addAddress?.city?.trim() &&
        addAddress?.state?.trim() &&
        addAddress?.country?.trim() &&
        addAddress?.contact?.name?.trim() &&
        addAddress?.contact?.mobileNo &&
        String(addAddress?.contact?.mobileNo).trim() !== "" &&
        String(addAddress?.contact?.mobileNo).length === 10 &&
        addAddress?.pincode &&
        String(addAddress?.pincode).trim() !== "" &&
        String(addAddress?.pincode).length === 6 &&
        isPincodeValid;

      if (allFieldsFilled) {
        setInputError(false);
      }
    }
  }, [addAddress, hasAttemptedSave, validationErrors]);

  return (
    <div className=" my-6">
      <div className="mx-4 font-medium text-[22px] p-2 border-b flex items-center justify-between">
        <div>ADD ADDRESS</div>
        <div className="cursor-pointer" onClick={() => setAddAddressModal(false)}>
          <img src={CloseIcon} alt="Close" width={24} height={24} />
        </div>
      </div>

      <div className="mx-2">
        <div className="m-[0.5rem] my-[1rem] bg-white">
          <div className="flex min-w-[90%] ">
            <div
              className="items-center flex border-2 rounded-md w-[100%] justify-between p-2 cursor-pointer"
              style={{
                boxShadow:
                  "0px 0px 0px 0px rgba(133, 133, 133, 0.05), 0px 6px 13px 0px rgba(133, 133, 133, 0.05)",
              }}
            >
              <div className="flex items-center gap-x-2 ">
                <img src={LocationIcon} width="40px" />
                <p>
                  <b>{capitalizeFirstLetter(addressType)}</b>
                </p>
              </div>
              {/* <div className="mr-2">
                      <img
                        src={DownArrowIcon}
                        className={`${
                          globalIndex === index ? "rotate-180" : ""
                        }`}
                      />
                    </div> */}
            </div>
          </div>
          <div className="border-2 border-t-0 max-h-[calc(100vh-200px)] overflow-y-auto overflow-x-hidden customScroll">
            {/* {globalIndex === index && ( */}
            <div className="p-[1rem]">
              {/* <div className="bg-white rounded-lg border border-black overflow-hidden shadow-lg relative">
                <div className="bg-black text-white p-4 h-1/3 flex items-center gap-x-2">
                  <img src={MagicLocationIcon} alt="Magic Location Icon" />
                  <div className="text-white text-[12px] font-Open">
                    Magic Address
                  </div>
                </div>

                <div className="relative h-[75px]  ">
                  <input
                    ref={inputRef}
                    type="text"
                    value={magicAddress}
                    // onKeyDown={}
                    onChange={(e: any) => {
                      setMagicAddress(e.target.value);
                    }}
                    className="magicAddressInput w-full removePaddingPlaceHolder"
                    style={{
                      position: "absolute",
                      border: "none",
                    }}
                    placeholder="Paste Address for the Magic"
                    title=""
                  />
                  <div>
                    <div className="absolute right-[1%] top-[70%] transform -translate-y-1/2 cursor-pointer">
                      {isAddressLoading ? (
                        <div className="flex justify-center items-center mr-3">
                          <Spinner />
                        </div>
                      ) : (
                        <img
                          src={AiIcon}
                          alt="Arrow"
                          onClick={() => handleButtonClick(targetAddress)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div> */}
              <div className="mt-5">
                {/* Contact and Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <FloatingLabelInput
                    placeholder="Contact"
                    type="tel"
                    value={String(addAddress?.contact?.mobileNo || "")}
                    onChangeCallback={(value) => {
                      const numericValue = value.replace(/\D/g, "");
                      setAddAddress({
                        ...addAddress,
                        contact: {
                          ...addAddress.contact,
                          mobileNo: numericValue,
                        },
                      });

                      const isValid =
                        value.length === 0 || isValidPhoneNumber(value);
                      setPhoneValidationErrors((prev: any) => ({
                        ...prev,
                        [targetAddress === "pickupAddress" ? "pickup" : "delivery"]: !isValid,
                      }));
                      clearValidationError(
                        targetAddress === "pickupAddress" ? "pickup" : "delivery",
                        "mobileNo"
                      );
                    }}
                    maxLength={10}
                    required
                    error={
                      phoneValidationErrors[
                        targetAddress === "pickupAddress" ? "pickup" : "delivery"
                      ] ||
                      (hasAttemptedSave &&
                        inputError &&
                        (!addAddress?.contact?.mobileNo ||
                          String(addAddress?.contact?.mobileNo).trim() === "" ||
                          String(addAddress?.contact?.mobileNo).length !== 10))
                    }
                    errorMessage={
                      phoneValidationErrors[
                        targetAddress === "pickupAddress" ? "pickup" : "delivery"
                      ]
                        ? "Please enter a valid 10-digit phone number starting with 6, 7, 8, or 9"
                        : "Contact number is required"
                    }
                  />

                  <FloatingLabelInput
                    placeholder="Name"
                    value={addAddress?.contact?.name || ""}
                    onChangeCallback={(value) => {
                      setAddAddress({
                        ...addAddress,
                        contact: {
                          ...addAddress.contact,
                          name: value,
                        },
                      });
                      clearValidationError(
                        targetAddress === "pickupAddress" ? "pickup" : "delivery",
                        "contactName"
                      );
                    }}
                    required
                    error={
                      hasAttemptedSave &&
                      inputError &&
                      !addAddress?.contact?.name?.trim()
                    }
                    errorMessage="Name is required"
                  />
                </div>

                {/* Pincode and Address Line 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <FloatingLabelInput
                    placeholder="Pin code"
                    value={String(addAddress?.pincode || "")}
                    onChangeCallback={(value) => {
                      handlePincodeChange(value, targetAddress);
                      clearValidationError(
                        targetAddress === "pickupAddress" ? "pickup" : "delivery",
                        "pincode"
                      );
                    }}
                    maxLength={6}
                    isPincodeField
                    required
                    error={
                      validationErrors[
                        targetAddress === "pickupAddress" ? "pickup" : "delivery"
                      ]?.pincode ||
                      (hasAttemptedSave &&
                        inputError &&
                        (!addAddress?.pincode ||
                          String(addAddress?.pincode).trim() === "" ||
                          String(addAddress?.pincode).length !== 6))
                    }
                    errorMessage={
                      validationErrors[
                        targetAddress === "pickupAddress" ? "pickup" : "delivery"
                      ]?.pincodeMessage || "Pin code is required"
                    }
                  />

                  <FloatingLabelInput
                    placeholder="Address Line 1"
                    value={addAddress?.flatNo || ""}
                    onChangeCallback={(value) => {
                      setAddAddress({ ...addAddress, flatNo: value });
                      clearValidationError(
                        targetAddress === "pickupAddress" ? "pickup" : "delivery",
                        "flatNo"
                      );
                    }}
                    required
                    error={
                      hasAttemptedSave && inputError && !addAddress?.flatNo?.trim()
                    }
                    errorMessage="Address Line 1 is required"
                  />
                </div>

                {/* Address Line 2 and Landmark */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <FloatingLabelInput
                    placeholder="Address Line 2"
                    value={addAddress?.locality || ""}
                    onChangeCallback={(value) => {
                      setAddAddress({ ...addAddress, locality: value });
                      clearValidationError(
                        targetAddress === "pickupAddress" ? "pickup" : "delivery",
                        "locality"
                      );
                    }}
                    required
                    error={
                      hasAttemptedSave && inputError && !addAddress?.locality?.trim()
                    }
                    errorMessage="Address Line 2 is required"
                  />

                  <FloatingLabelInput
                    placeholder="Landmark"
                    value={addAddress?.landmark || ""}
                    onChangeCallback={(value) => {
                      setAddAddress({ ...addAddress, landmark: value });
                      clearValidationError(
                        targetAddress === "pickupAddress" ? "pickup" : "delivery",
                        "landmark"
                      );
                    }}
                    required
                    error={
                      hasAttemptedSave && inputError && !addAddress?.landmark?.trim()
                    }
                    errorMessage="Landmark is required"
                  />
                </div>

                {/* City and State */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <FloatingLabelInput
                    placeholder="City"
                    value={addAddress?.city || ""}
                    readOnly
                  />

                  <FloatingLabelInput
                    placeholder="State"
                    value={addAddress?.state || ""}
                    readOnly
                  />
                </div>

                {/* GST and Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <FloatingLabelInput
                    placeholder="GST No (Optional)"
                    value={addAddress?.gstNumber || ""}
                    onChangeCallback={(value) => {
                      setAddAddress({ ...addAddress, gstNumber: value });
                      setValidationErrors((prev: any) => ({
                        ...prev,
                        [targetAddress === "pickupAddress" ? "pickup" : "delivery"]: {
                          ...prev[targetAddress === "pickupAddress" ? "pickup" : "delivery"],
                          gstLength: false,
                        },
                      }));
                    }}
                  />

                  <FloatingLabelInput
                    placeholder="Email ID (Optional)"
                    type="email"
                    value={addAddress?.contact?.emailId || ""}
                    onChangeCallback={(value) => {
                      setAddAddress({
                        ...addAddress,
                        contact: {
                          ...addAddress.contact,
                          emailId: value,
                        },
                      });
                    }}
                  />
                </div>
              </div>
              
              {/* SAVE Button inside scrollable container */}
              <div className="mt-6 mb-4">
                {isLoading ? (
                  <div className="flex w-[100%] justify-center items-center py-4">
                    <Spinner />
                  </div>
                ) : (
                  <div
                    className={`flex w-[100%] items-center py-2 justify-center border-2 rounded-md text-white ${
                      (() => {
                        const addressTypeKey = targetAddress === "pickupAddress" ? "pickup" : "delivery";
                        const isPincodeValid = !validationErrors[addressTypeKey]?.pincode && 
                          addAddress?.pincode && 
                          String(addAddress?.pincode).length === 6 &&
                          addAddress?.city?.trim() &&
                          addAddress?.state?.trim();
                        const allFieldsValid =
                          addAddress?.flatNo?.trim() &&
                          addAddress?.landmark?.trim() &&
                          addAddress?.city?.trim() &&
                          addAddress?.state?.trim() &&
                          addAddress?.country?.trim() &&
                          addAddress?.contact?.name?.trim() &&
                          addAddress?.contact?.mobileNo &&
                          String(addAddress?.contact?.mobileNo).trim() !== "" &&
                          String(addAddress?.contact?.mobileNo).length === 10 &&
                          addAddress?.pincode &&
                          String(addAddress?.pincode).trim() !== "" &&
                          String(addAddress?.pincode).length === 6 &&
                          isPincodeValid;
                        return allFieldsValid ? "bg-black cursor-pointer" : "bg-[#D2D2D2] cursor-not-allowed";
                      })()
                    }`}
                    onClick={() => {
                      const addressTypeKey = targetAddress === "pickupAddress" ? "pickup" : "delivery";
                      const isPincodeValid = !validationErrors[addressTypeKey]?.pincode && 
                        addAddress?.pincode && 
                        String(addAddress?.pincode).length === 6 &&
                        addAddress?.city?.trim() &&
                        addAddress?.state?.trim();
                      const allFieldsValid =
                        addAddress?.flatNo?.trim() &&
                        addAddress?.landmark?.trim() &&
                        addAddress?.city?.trim() &&
                        addAddress?.state?.trim() &&
                        addAddress?.country?.trim() &&
                        addAddress?.contact?.name?.trim() &&
                        addAddress?.contact?.mobileNo &&
                        String(addAddress?.contact?.mobileNo).trim() !== "" &&
                        String(addAddress?.contact?.mobileNo).length === 10 &&
                        addAddress?.pincode &&
                        String(addAddress?.pincode).trim() !== "" &&
                        String(addAddress?.pincode).length === 6 &&
                        isPincodeValid;
                      if (allFieldsValid && !inputError) {
                        createAddress();
                      }
                    }}
                  >
                    SAVE
                  </div>
                )}
              </div>
            </div>
            {/* )} */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddAddress;
